import React, { useState, useEffect } from 'react';
import './DoctorPatients.css';
import { collection, query, where, onSnapshot } from 'firebase/firestore';
import { db } from '../../../firebase/firebaseConfig';
import { useAuth } from '../../../context/AuthContext';
import { useNavigate } from 'react-router-dom';
import { FaSearch, FaSortAlphaDown, FaSortAlphaUp, FaUserInjured } from 'react-icons/fa';
import { Card, Button } from 'react-bootstrap';

function DoctorPatients() {
    const { currentUser } = useAuth();
    const doctorId = currentUser?.uid;
    const navigate = useNavigate();

    const [appointments, setAppointments] = useState([]);
    const [searchTerm, setSearchTerm] = useState('');
    const [sortOrder, setSortOrder] = useState('asc');
    const [filterDate, setFilterDate] = useState('');

    useEffect(() => {
        if (!doctorId) {
            console.warn('Doctor ID is undefined, cannot fetch appointments.');
            return;
        }

        const q = query(
            collection(db, 'appointments'),
            where('doctorId', '==', doctorId),
            where('status', '==', 'Completed')
        );

        const unsubscribe = onSnapshot(q, (snapshot) => {
            const data = snapshot.docs.map((docSnap) => {
                const appointmentData = docSnap.data();
                return {
                    id: docSnap.id,
                    ...appointmentData,
                    patientName: appointmentData.fullName || 'Unknown',
                    patientAge: appointmentData.age || 'N/A',
                    patientGender: appointmentData.gender || 'N/A',
                    patientBloodGroup: appointmentData.bloodGroup || 'N/A',
                    patientEmail: appointmentData.email || 'N/A',
                    patientPhone: appointmentData.phone || 'N/A',
                    patientAddress: appointmentData.address || 'N/A',
                };
            });
            setAppointments(data);
        });

        return () => unsubscribe();
    }, [doctorId]);

    const filteredAppointments = appointments
        .filter((appointment) =>
            appointment.patientName.toLowerCase().includes(searchTerm.toLowerCase())
        )
        .filter((appointment) =>
            filterDate ? appointment.appointmentDate === filterDate : true
        )
        .sort((a, b) => {
            if (sortOrder === 'asc') {
                return a.patientName.localeCompare(b.patientName);
            } else {
                return b.patientName.localeCompare(a.patientName);
            }
        });

    const handleViewPatientDetails = (appointmentId, patientId) => {
        navigate(`/doctor/patient-details/${appointmentId}/${patientId}`);
    };

    return (
        <div className="container-fluid py-4">
            <h3 className="mb-4 text-primary fw-bold text-center" style={{ fontFamily: 'Poppins, sans-serif' }}>
                Consulted Patients
            </h3>

            <Card className="shadow-sm border-0 rounded-3 mb-4">
                <Card.Body className="d-flex flex-wrap justify-content-between align-items-center gap-3">
                    <div className="d-flex align-items-center bg-light rounded-pill px-3 py-2" style={{ flex: '1 1 300px' }}>
                        <FaSearch className="text-muted me-2" />
                        <input
                            type="text"
                            placeholder="Search by Patient Name"
                            value={searchTerm}
                            onChange={(e) => setSearchTerm(e.target.value)}
                            className="form-control border-0 bg-transparent shadow-none"
                        />
                    </div>

                    <div className="d-flex gap-3">
                        <input
                            type="date"
                            value={filterDate}
                            onChange={(e) => setFilterDate(e.target.value)}
                            className="form-control rounded-pill px-3 shadow-sm border-0 bg-light"
                            title="Filter by Date"
                        />

                        <button
                            className="btn btn-outline-primary rounded-pill px-4 shadow-sm d-flex align-items-center"
                            onClick={() => setSortOrder(sortOrder === 'asc' ? 'desc' : 'asc')}
                            title="Sort by Name"
                        >
                            {sortOrder === 'asc' ? <><FaSortAlphaDown className="me-2"/> Sort A-Z</> : <><FaSortAlphaUp className="me-2"/> Sort Z-A</>}
                        </button>
                    </div>
                </Card.Body>
            </Card>

            <Card className="shadow-sm border-0 rounded-3">
                <Card.Body className="p-0">
                    {filteredAppointments.length === 0 ? (
                        <div className="text-center text-muted py-5">
                            <FaUserInjured size={60} className="text-secondary mb-3 opacity-50 d-block mx-auto" />
                            <h5 className="fw-bold">No Completed Consultations Found</h5>
                            <p>Try adjusting your search, filter, or date selection.</p>
                        </div>
                    ) : (
                        <div className="table-responsive">
                            <table className="table table-hover align-middle mb-0">
                                <thead className="table-light">
                                    <tr>
                                        <th className="px-4 py-3">Patient Code</th>
                                        <th className="py-3">Patient Name</th>
                                        <th className="py-3">Last Visit</th>
                                        <th className="py-3">Time</th>
                                        <th className="py-3">Doctor</th>
                                        <th className="py-3">Status</th>
                                        <th className="px-4 py-3 text-end">Action</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {filteredAppointments.map((appointment) => (
                                        <tr key={appointment.id}>
                                            <td className="px-4"><span className="fw-medium text-dark">{appointment.patientCode || 'N/A'}</span></td>
                                            <td>{appointment.patientName}</td>
                                            <td>{appointment.appointmentDate}</td>
                                            <td>{appointment.appointmentTime}</td>
                                            <td>Dr. {appointment.doctor}</td>
                                            <td>
                                                <span className={`badge bg-${appointment.status === 'Completed' ? 'success' : 'primary'} text-white px-2 py-1 rounded-pill`}>
                                                    {appointment.status}
                                                </span>
                                            </td>
                                            <td className="px-4 text-end">
                                                <button
                                                    className="btn btn-sm btn-primary rounded-pill px-3 shadow-sm"
                                                    onClick={() =>
                                                        handleViewPatientDetails(appointment.id, appointment.patientId)
                                                    }
                                                >
                                                    View Details
                                                </button>
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>
                    )}
                </Card.Body>
            </Card>
        </div>
    );
}

export default DoctorPatients;
